// FILE: src/blocks/blueprint-runner/frontend.js
// GOAL: Mount the BlueprintApp React root on every rendered blueprint-runner block instance.

import apiFetch from '@wordpress/api-fetch';
import domReady from '@wordpress/dom-ready';
import { createRoot } from 'react-dom/client';
import BlueprintApp from '../../blueprint/app/BlueprintApp.jsx';

/**
 * Normalize any incoming value to a string.
 *
 * This avoids leaking null/undefined into props that are expected
 * to behave like plain strings.
 *
 * @param {*} v Raw value from a dataset attribute or other source.
 * @return {string}
 */
function asString(v) {
  return v == null ? '' : String(v);
}

/**
 * Find every blueprint runner mount node on the page
 * and mount a separate React root into each one.
 *
 * FLOW:
 * - Query all server-rendered blueprint runner placeholders.
 * - Skip nodes that have already been mounted.
 * - Read bootstrap values from data attributes.
 * - Render BlueprintApp with those initial props.
 *
 * @return {void}
 */
function mountAll() {
  // Each rendered block instance outputs a mount node with this marker.
  const nodes = document.querySelectorAll('[data-blueprint-runner="1"]');

  nodes.forEach((node) => {
    // Skip invalid nodes or nodes that were already mounted earlier.
    if (!node || node.__dvBlueprintMounted) return;

    // Mark the node so the same React app is not mounted twice.
    node.__dvBlueprintMounted = true;

    // Read initial bootstrap values from the server-rendered dataset.
    const packId = asString(node.dataset.packId).trim();
    const wpApiRoot = asString(node.dataset.wpApiRoot).trim();
    const wpNonce = asString(node.dataset.wpNonce).trim();

    if (wpNonce) {
      apiFetch.use(apiFetch.createNonceMiddleware(wpNonce));
    }

    // Convert the dataset flag to a real boolean.
    const showDescription = String(node.dataset.showDescription) === '1';

    // Mount the top-level Blueprint React app into this block instance.
    createRoot(node).render(
      <BlueprintApp
        initialPackId={packId}
        wpApiRoot={wpApiRoot}
        wpNonce={wpNonce}
        showDescription={showDescription}
      />
    );
  });
}

// Wait until the DOM is ready before attempting to find mount nodes.
domReady(mountAll);
